import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useOrderFormContext } from '../OrderFormProvider';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CouponsFormSection from './CouponsFormSection';
import OrderItemsFormSection from './OrderItemsFormSection';
import OrderTagsFormSection from '../OrderTagsFormSection';
import OrderTotalsFormSection from './OrderTotalsFormSection';
import PricingAtFormSection from '../PricingAtFormSection';
import ClientFormSection from '@afosto/app-components-react/ClientFormSection';
import ReferenceFormSection from '@afosto/app-components-react/ReferenceFormSection';
import ContactFormSection from '@afosto/app-components-react/ContactFormSection';
import OrganisationFormSection from '@afosto/app-components-react/OrganisationFormSection';
import OrderAddressFormSection from '@afosto/app-components-react/OrderAddressFormSection';
import PaymentMethodFormSection from '@afosto/app-components-react/PaymentMethodFormSection';
import ProgressDialog from '@afosto/app-components-react/ProgressDialog';
import ShippingMethodFormSection from '@afosto/app-components-react/ShippingMethodFormSection';
import getUnixTimestamp from '@afosto/utils/date/getUnixTimestamp';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import translations from './translations';

const OrderForm = ({ onSave }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const orderFormContext = useOrderFormContext();
  const orderFormContextRef = useRef(orderFormContext);
  const {
    addCoupon,
    availableAddresses,
    billing,
    billingAddress,
    changeBilling,
    changeBillingAddress,
    changeClient,
    changeContact,
    changeOrganisation,
    changeReference,
    changeShipping,
    changeShippingAddress,
    client,
    contact,
    coupons,
    items,
    isCreatingCalculation,
    order,
    organisation,
    shipping,
    setIsCreatingCalculation,
    shippingAddress,
    reference,
    removeCoupon,
    setReference,
    orderTags,
    changeOrderTags,
    type,
    pricingAt,
    setPricingAt,
    updateContact,
    updateOrganisation,
  } = orderFormContext;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isPurchase = type === 'purchase';
  const orderId = order?.id;
  const updatedAt = order?.updated_at;
  const itemCount = items?.length;
  const showSaveWithCalculation = type !== 'return' && type !== 'cancellation';

  useEffect(() => {
    orderFormContextRef.current = orderFormContext;
  });

  const fetchProjection = useCallback(async () => {
    const {
      setHasProjectionError,
      setIsFetchingProjection,
      setProjection,
      pricingAt: pricingAtDate,
    } = orderFormContextRef.current ?? {};

    setHasProjectionError(false);

    try {
      setIsFetchingProjection(true);

      const response = await ordersApi.getOrderProjection(orderId, {
        pricingAt: pricingAtDate ? getUnixTimestamp(pricingAtDate) : null,
      });
      const { data = {} } = response;

      setProjection(data);
      return data;
    } catch (err) {
      setProjection({});
      setHasProjectionError(true);
      return {};
    } finally {
      setIsFetchingProjection(false);
    }
  }, [orderId]);

  useEffect(() => {
    const { items: orderItems = [] } = orderFormContextRef.current ?? {};

    if (orderItems.length) {
      fetchProjection();
    }
  }, [fetchProjection, updatedAt, pricingAt]);

  const handlePricingAt = async value => {
    setPricingAt(value);
  };

  const isValid = () => {
    if (!client?.id) {
      return false;
    }

    return !!items?.length;
  };

  const handleSave = () => {
    setIsSubmitted(true);

    if (!isValid()) {
      return;
    }

    if (onSave && typeof onSave === 'function') {
      onSave();
    }
  };

  const handleSaveWithCalculation = async () => {
    try {
      setIsSubmitted(true);

      if (!isValid()) {
        return;
      }

      setIsCreatingCalculation(true);

      await ordersApi.createCalculation(order?.id, {
        expires_at: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
        reference: order?.id,
        ...(pricingAt ? { pricing_at: pricingAt } : {}),
      });

      handleSave();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.calculationNotCreated), {
        variant: 'error',
      });
    } finally {
      setIsCreatingCalculation(false);
    }
  };

  return (
    <>
      <ReferenceFormSection
        onBlur={changeReference}
        onChange={event => setReference(event?.target?.value)}
        value={reference}
      />
      <OrderTagsFormSection onChange={changeOrderTags} value={orderTags} />
      <ClientFormSection
        onChange={changeClient}
        value={client}
        showError={isSubmitted && !client?.id}
      />
      <ContactFormSection onChange={changeContact} value={contact} />
      <OrganisationFormSection
        contact={contact}
        onChange={changeOrganisation}
        onUpdateContact={updateContact}
        value={organisation}
      />
      <OrderAddressFormSection
        address={billingAddress}
        availableAddresses={availableAddresses}
        contact={contact}
        organisation={organisation}
        onChange={changeBillingAddress}
        onUpdateContact={updateContact}
        onUpdateOrganisation={updateOrganisation}
        title={intl.formatMessage(translations.addressFormSection.billing.title)}
        type="billing"
      />
      <OrderAddressFormSection
        address={shippingAddress}
        availableAddresses={availableAddresses}
        contact={contact}
        organisation={organisation}
        onChange={changeShippingAddress}
        onUpdateContact={updateContact}
        onUpdateOrganisation={updateOrganisation}
        title={intl.formatMessage(
          translations.addressFormSection[isPurchase ? 'source' : 'target'].title,
        )}
        type="shipping"
      />
      {shippingAddress && client && (
        <ShippingMethodFormSection
          clientId={client?.client_id}
          setShipping={changeShipping}
          shippingAddress={shippingAddress}
          shipping={shipping}
          showDueAt
        />
      )}
      {client && (
        <PaymentMethodFormSection
          clientId={client?.client_id}
          setPaymentMethod={changeBilling}
          paymentMethod={billing}
        />
      )}

      <PricingAtFormSection onChange={handlePricingAt} value={pricingAt} />

      <OrderItemsFormSection fetchProjection={fetchProjection} />
      <CouponsFormSection coupons={coupons} onAddCoupon={addCoupon} onRemoveCoupon={removeCoupon} />
      <OrderTotalsFormSection fetchProjection={fetchProjection} />
      <Box mt={2} mb={6} display="flex" justifyContent="flex-end">
        <Button
          color="primary"
          variant="contained"
          disabled={!itemCount || isCreatingCalculation}
          onClick={handleSave}
          style={{ marginRight: 8 }}
        >
          <FormattedMessage {...translations.formActions.saveWithoutCalculation} />
        </Button>
        {showSaveWithCalculation && (
          <Button
            color="primary"
            variant="contained"
            disabled={!itemCount || isCreatingCalculation}
            onClick={handleSaveWithCalculation}
          >
            <FormattedMessage {...translations.formActions.save} />
          </Button>
        )}
      </Box>
      <ProgressDialog
        isOpen={isCreatingCalculation}
        message={intl.formatMessage(translations.createCalculationProgressDialog.message)}
      />
    </>
  );
};

OrderForm.propTypes = {
  onSave: PropTypes.func,
};

OrderForm.defaultProps = {
  onSave: undefined,
};

export default OrderForm;
